/**
 * What the composer will hand to the picker, and what it says when it refuses
 * (ENG-4185).
 *
 * Mirrors `applications/amazon_connect/support_cases/sniff.py`. This is **UX only**:
 * the server sniffs the bytes and is the boundary. The point of the mirror is a
 * refusal before a 20 MB upload rather than after one, and a sentence the user reads
 * in the panel instead of a 415 they cannot interpret.
 */

export const MAX_ATTACHMENT_BYTES = 20 * 1024 * 1024;
export const MAX_ATTACHMENT_MB = 20;
export const MAX_FILES_PER_MESSAGE = 5;

/** The extensions Thor accepts. Order is the order the sentence lists them. */
export const ALLOWED_EXTENSIONS = [
  'pdf',
  'docx',
  'xlsx',
  'pptx',
  'odt',
  'ods',
  'odp',
  'csv',
  'txt',
  'md',
  'png',
  'jpg',
  'jpeg',
  'gif',
  'webp',
  'heic',
  'heif',
  'tif',
  'tiff',
  'bmp',
] as const;

/** What the picker's `accept` attribute offers. */
export const ATTACHMENT_ACCEPT = ALLOWED_EXTENSIONS.map(
  (extension) => `.${extension}`,
).join(',');

/** The sentence the panel shows, and the one an inline rejection repeats. */
export const SUPPORTED_FILES_SUMMARY =
  'PDF, Word (.docx), Excel (.xlsx), PowerPoint (.pptx), OpenDocument, CSV, text, Markdown, and images (PNG, JPEG, GIF, WebP, HEIC, TIFF, BMP)';

/** Extensions refused on purpose, named so the refusal can explain itself. */
const EXPLAINED_REFUSALS: Record<string, string> = {
  svg: 'an image format that can carry scripts',
  html: 'a web page, which can carry scripts',
  htm: 'a web page, which can carry scripts',
  zip: 'a compressed archive',
  rar: 'a compressed archive',
  '7z': 'a compressed archive',
  docm: 'a Word file with macros',
  xlsm: 'an Excel file with macros',
  pptm: 'a PowerPoint file with macros',
  doc: 'an older Word format',
  xls: 'an older Excel format',
  ppt: 'an older PowerPoint format',
  rtf: 'a rich-text format',
  exe: 'a program',
  js: 'a script',
  sh: 'a script',
};

export const extensionOf = (name: string): string => {
  const trimmed = (name || '').trim();
  if (!trimmed.includes('.')) {
    return '';
  }
  return trimmed.split('.').pop()?.toLowerCase() ?? '';
};

/**
 * Why this file cannot be attached, or `null` when it can.
 *
 * A sentence, not a code: it is rendered verbatim next to the composer.
 */
export const rejectionFor = (file: File): string | null => {
  const extension = extensionOf(file.name);
  if (!extension) {
    return `That file has no extension, so we cannot tell what it is. Supported files: ${SUPPORTED_FILES_SUMMARY}.`;
  }
  if (!(ALLOWED_EXTENSIONS as readonly string[]).includes(extension)) {
    const explained = EXPLAINED_REFUSALS[extension];
    if (explained) {
      return `We cannot accept ${explained}. Supported files: ${SUPPORTED_FILES_SUMMARY}.`;
    }
    return `We cannot accept .${extension} files. Supported files: ${SUPPORTED_FILES_SUMMARY}.`;
  }
  if (file.size === 0) {
    return 'That file is empty.';
  }
  if (file.size > MAX_ATTACHMENT_BYTES) {
    return `That file is larger than ${MAX_ATTACHMENT_MB} MB. Compress it or split it and try again.`;
  }
  return null;
};

/** A short, human size for a chip: "184 KB", "1.2 MB". */
export const formatFileSize = (bytes: number): string => {
  if (!bytes) {
    return '0 KB';
  }
  if (bytes < 1024 * 1024) {
    return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** An icon-ish label for a file type: "PDF", "PNG", "CSV". */
export const fileKindLabel = (name: string): string => {
  const extension = extensionOf(name);
  return extension ? extension.toUpperCase() : 'FILE';
};
